import { useState, useEffect, useRef } from 'react'
import { useDebounce } from '@/utils/fetch'
import apiFetch from '@/services/apiService'
import SuggestionsPortal from '@/components/SuggestionsPortal'

import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'

const SuggestionField = ({type = "", prestataireField, originalData = null, 
    label = "Prestataire",
    placeholder = "Rechercher un prestataire",
    onNewPrestataire = () => {},
    validator = {}
  }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedPrestataire, setSelectedPrestataire] = useState(null)
  const inputRef = useRef(null)

  const name = prestataireField.name

  // Récupérer les suggestions de prestataires
  const fetchSuggestions = useDebounce(async (term) => {
    if (!term || term.length < 2) {
      setSuggestions([])
      return
    }
    
    setLoading(true)
    try {
      let query = { term }
      if(type) { 
        query = {...query, type }
      }
      
      const response = await apiFetch()
        .url(`prestataires/search`)
        .query(query)
        .get()
      
      setSuggestions(response.data || [])
    } catch (error) {
      console.error('Erreur lors de la recherche de prestataires:', error)
      setSuggestions([])
    } finally {
      setLoading(false)
    }
  }, 300)

  // Charger les détails du prestataire sélectionné
  useEffect(() => {
    setSelectedPrestataire(originalData)
    setSearchTerm(originalData?.nom_complet || '')
  }, [originalData])

  // Gérer le changement dans le champ de recherche
  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    fetchSuggestions(value)
    setShowSuggestions(true)
    
    // Si le champ est vidé, réinitialiser le prestataire sélectionné
    if (!value) {
      prestataireField.onChange('')
      setSelectedPrestataire(null)
    }
  }

  // Sélectionner un prestataire dans les suggestions
  const handleSelectPrestataire = (prestataire) => {
    prestataireField.onChange(prestataire)
    setSearchTerm(prestataire.nom_complet || '')
    setSelectedPrestataire(prestataire)
    setShowSuggestions(false)
  } 

  // Réinitialiser le prestataire sélectionné
  const resetSelectedPrestataire = () => {
    prestataireField.onChange('')
    setSelectedPrestataire(null)
    setSearchTerm('')
  }

  return (
    <div className="relative suggestions-container">
      <label className={`input w-full ${validator.hasError ? 'validator' : ''}`}
        {...(validator.hasError && validator.hasError(name) ? {'aria-invalid': true} : {})}
      >
        <span className="label">{label}</span>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={placeholder}
          className="w-full"
          ref={inputRef}
        />
        {loading && <span className="loading loading-spinner loading-sm text-base-content/70 absolute right-2 top-1/2 transform -translate-y-1/2"></span>}
        {!loading && selectedPrestataire && (
          <button
          type="button"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-base-content/70 rounded-full w-4 h-4 transition-colors duration-200 ease-in hover:bg-gray-200 focus:outline-none focus:bg-gray-300 dark:text-white dark:hover:bg-dark-500 dark:focus:bg-dark-450 hover:bg-gray-200"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              resetSelectedPrestataire()
            }}
          >
            <XMarkIcon />  
          </button>
        )}
      </label>
      {validator.getErrorMessage && (
        <div className="validator-hint hidden empty:py-[0.6rem]"
          style={{marginTop: '0.5em'}}
        >
          {validator.getErrorMessage(name)}
        </div>
      )}

      <SuggestionsPortal
        show={showSuggestions && (suggestions.length > 0 || (searchTerm.length >= 2 && !loading))}
        anchorRef={inputRef}
      >
      <div className="absolute z-10 w-full bg-base-100 shadow-lg rounded-lg mt-1 p-2 border border-base-300 max-h-60 overflow-y-auto">
      {suggestions.length > 0 ? suggestions.map(prestataire => (
        <div 
          key={prestataire.id}
          className="p-2 hover:bg-base-200 cursor-pointer"
          onClick={() => handleSelectPrestataire(prestataire)}
        >
          <div className="font-medium">{prestataire.nom_complet}</div>
          <div className="text-sm text-base-content/70">
            {prestataire.email || prestataire.telephone ? `${prestataire.email}${prestataire.telephone ? ` / ${prestataire.telephone} ` : ''}` : 'Aucune information de contact'}
          </div>
        </div>
      )) : (
        <p>Aucun prestataire trouvé</p>
      )}
      <div>
        <button className="btn btn-primary mt-1 w-full"
          onClick={ (event) => {
            event.preventDefault()
            event.stopPropagation()
            onNewPrestataire()
          }}
        >
          <PlusIcon className='h-4 w-4 mr-1' />
          Créer un prestataire
        </button>
      </div>
      </div>
      </SuggestionsPortal> 
    </div>
  )
}
export default SuggestionField